import { create } from 'zustand'

import { ChatAttachment, StagedAttachment } from '../core/types/chat.types'
import { generateId } from '../core/services/idGenerator'
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENT_FILE_SIZE,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_CONCURRENT_UPLOADS,
  formatAttachmentSizeLimit,
  getAttachmentKind,
  isAllowedAttachment,
} from '../core/utils/attachment'

type StageFileFn = (file: File, onProgress?: (progress: number) => void) => Promise<ChatAttachment>

type MaxAttachmentStoreState = {
  attachments: StagedAttachment[]
  error: string | null
}

type MaxAttachmentStoreActions = {
  addFiles: (fileInput: FileList | File[], stageFile: StageFileFn) => void
  removeAttachment: (id: string) => void
  clearAll: () => void
  markError: (message: string) => void
}

type MaxAttachmentStore = MaxAttachmentStoreState & MaxAttachmentStoreActions

const updateAttachmentById = (
  attachments: StagedAttachment[],
  id: string,
  updates: Omit<Partial<StagedAttachment>, 'id'>
): StagedAttachment[] =>
  attachments.map((attachment) =>
    attachment.id === id ? ({ ...attachment, ...updates } as StagedAttachment) : attachment
  )

export const hasStagedAttachments = (attachments: StagedAttachment[]): boolean => attachments.length > 0

export const areAttachmentsUploading = (attachments: StagedAttachment[]): boolean =>
  attachments.some(
    (attachment) => attachment.status === 'uploading' || attachment.status === 'queued'
  )

export const doAttachmentsHaveErrors = (attachments: StagedAttachment[]): boolean =>
  attachments.some((attachment) => attachment.status === 'error')

export const getRemainingAttachmentSlots = (attachments: StagedAttachment[]): number =>
  Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - attachments.length)

export const areAttachmentsReadyToSend = (attachments: StagedAttachment[]): boolean =>
  !hasStagedAttachments(attachments) || attachments.every((attachment) => attachment.status === 'ready')

export const getReadyAttachments = (attachments: StagedAttachment[]): ChatAttachment[] =>
  attachments
    .filter((attachment) => attachment.status === 'ready' && attachment.uniqueFilename)
    .map((attachment) => ({
      uniqueFilename: attachment.uniqueFilename!,
      userFilename: attachment.userFilename,
      contentType: attachment.contentType,
      size: attachment.size,
      kind: attachment.kind,
    }))

// The uploader of the latest addFiles call; queued files start with it when a slot frees up.
let queuedStageFile: StageFileFn | null = null

const activeUploadCount = (): number =>
  useMaxAttachmentStore
    .getState()
    .attachments.filter((attachment) => attachment.status === 'uploading').length

const pumpUploadQueue = (): void => {
  let free = MAX_CONCURRENT_UPLOADS - activeUploadCount()
  if (free <= 0) return

  const stageFile = queuedStageFile
  if (!stageFile) return

  useMaxAttachmentStore.getState().attachments.some((attachment) => {
    if (free === 0) return true
    if (attachment.status !== 'queued') return false
    void startUpload(attachment.id, stageFile)
    free -= 1
    return false
  })
}

const startUpload = async (id: string, stageFile: StageFileFn): Promise<void> => {
  const staged = useMaxAttachmentStore
    .getState()
    .attachments.find((attachment) => attachment.id === id)
  if (!staged || !staged.file) return

  const fileToUpload = staged.file
  useMaxAttachmentStore.setState((state) => ({
    attachments: updateAttachmentById(state.attachments, id, {
      status: 'uploading',
      progress: 25,
    }),
  }))

  try {
    const metadata = await stageFile(fileToUpload, (uploadProgress) => {
      useMaxAttachmentStore.setState((state) => ({
        attachments: updateAttachmentById(state.attachments, id, {
          progress: 25 + uploadProgress * 0.75,
        }),
      }))
    })

    useMaxAttachmentStore.setState((state) => ({
      attachments: updateAttachmentById(state.attachments, id, {
        uniqueFilename: metadata.uniqueFilename,
        progress: 100,
        status: 'ready',
        file: undefined,
        kind: metadata.kind || staged.kind,
      }),
    }))
  } catch (error) {
    useMaxAttachmentStore.setState((state) => ({
      attachments: updateAttachmentById(state.attachments, id, {
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Failed to upload attachment',
      }),
    }))
  } finally {
    pumpUploadQueue()
  }
}

const useMaxAttachmentStore = create<MaxAttachmentStore>((set, get) => ({
  attachments: [],
  error: null,

  addFiles: (fileInput, stageFile) => {
    const incoming = Array.from(fileInput as ArrayLike<File>)
    if (!incoming.length) return

    queuedStageFile = stageFile

    incoming.forEach((file) => {
      const { attachments } = get()

      if (attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
        set({
          error: `You can only attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files`,
        })
        return
      }

      if (!isAllowedAttachment(file)) {
        set({
          error: `${file.name} is not an allowed file type. Allowed types: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(
            ', '
          )}`,
        })
        return
      }

      if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
        set({
          error: `${file.name} exceeds the ${formatAttachmentSizeLimit()} limit`,
        })
        return
      }

      const staged: StagedAttachment = {
        id: generateId(),
        file,
        userFilename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        kind: getAttachmentKind(file.type || '', file.name),
        progress: 0,
        status: 'queued',
      }

      set((state) => ({
        attachments: [...state.attachments, staged],
        error: null,
      }))
    })

    pumpUploadQueue()
  },

  removeAttachment: (id) => {
    set((state) => {
      const nextAttachments = state.attachments.filter((attachment) => attachment.id !== id)
      return {
        attachments: nextAttachments,
        error: nextAttachments.length === 0 ? null : state.error,
      }
    })
    pumpUploadQueue()
  },

  clearAll: () =>
    set({
      attachments: [],
      error: null,
    }),

  markError: (message) =>
    set({
      error: message,
    }),
}))

export default useMaxAttachmentStore
